import React, { useState, useRef, useEffect } from 'react';
import './VoiceNote.css';

// Supported languages - Indian + Global
const LANGUAGES = [
  // Global Languages
  { code: 'en', name: 'English', speechCode: 'en-US', category: 'global' },
  { code: 'es', name: 'Spanish', speechCode: 'es-ES', category: 'global' },
  { code: 'fr', name: 'French', speechCode: 'fr-FR', category: 'global' },
  { code: 'de', name: 'German', speechCode: 'de-DE', category: 'global' },
  { code: 'it', name: 'Italian', speechCode: 'it-IT', category: 'global' },
  { code: 'pt', name: 'Portuguese', speechCode: 'pt-PT', category: 'global' },
  { code: 'ru', name: 'Russian', speechCode: 'ru-RU', category: 'global' },
  { code: 'ja', name: 'Japanese', speechCode: 'ja-JP', category: 'global' },
  { code: 'ko', name: 'Korean', speechCode: 'ko-KR', category: 'global' },
  { code: 'zh', name: 'Chinese', speechCode: 'zh-CN', category: 'global' },
  { code: 'ar', name: 'Arabic', speechCode: 'ar-SA', category: 'global' },
  { code: 'tr', name: 'Turkish', speechCode: 'tr-TR', category: 'global' },
  { code: 'nl', name: 'Dutch', speechCode: 'nl-NL', category: 'global' },
  { code: 'pl', name: 'Polish', speechCode: 'pl-PL', category: 'global' },
  { code: 'th', name: 'Thai', speechCode: 'th-TH', category: 'global' },
  { code: 'vi', name: 'Vietnamese', speechCode: 'vi-VN', category: 'global' },
  { code: 'id', name: 'Indonesian', speechCode: 'id-ID', category: 'global' },
  { code: 'ms', name: 'Malay', speechCode: 'ms-MY', category: 'global' },
  // Indian Languages
  { code: 'hi', name: 'Hindi', speechCode: 'hi-IN', category: 'indian' },
  { code: 'ta', name: 'Tamil', speechCode: 'ta-IN', category: 'indian' },
  { code: 'te', name: 'Telugu', speechCode: 'te-IN', category: 'indian' },
  { code: 'kn', name: 'Kannada', speechCode: 'kn-IN', category: 'indian' },
  { code: 'ml', name: 'Malayalam', speechCode: 'ml-IN', category: 'indian' },
  { code: 'mr', name: 'Marathi', speechCode: 'mr-IN', category: 'indian' },
  { code: 'bn', name: 'Bengali', speechCode: 'bn-IN', category: 'indian' },
  { code: 'gu', name: 'Gujarati', speechCode: 'gu-IN', category: 'indian' },
  { code: 'pa', name: 'Punjabi', speechCode: 'pa-IN', category: 'indian' },
];

const VoiceNote = ({
  onSave,
  originalLanguage = 'en',
  targetLanguage = 'hi',
  mode = 'record' // 'record' for customer, 'play' for rider
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [originalText, setOriginalText] = useState('');
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState(originalLanguage);
  const [targetLang, setTargetLang] = useState(targetLanguage);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState('');
  const [audioBlob, setAudioBlob] = useState(null);
  const [interimText, setInterimText] = useState('');
  const [voiceToVoiceMode, setVoiceToVoiceMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Check if running on mobile (HTTP without microphone access)
  const isMobileHTTP = typeof window !== 'undefined' &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
    window.location.protocol !== 'https:';

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const voicesRef = useRef([]);
  const isRecordingRef = useRef(false); // Use ref to track recording state reliably

  // Preload speech synthesis voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Load voices - they may not be available immediately
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          voicesRef.current = voices;
          console.log('Loaded', voices.length, 'voices');
        }
      };

      // Initial load
      loadVoices();

      // Chrome loads voices asynchronously
      window.speechSynthesis.onvoiceschanged = loadVoices;

      // Also try after a delay (some browsers need this)
      setTimeout(loadVoices, 500);
      setTimeout(loadVoices, 1000);
    }
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interim = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interim += transcript;
          }
        }

        // Show interim results while speaking
        setInterimText(interim);

        if (finalTranscript) {
          setOriginalText(prev => (prev + ' ' + finalTranscript).trim());
          setInterimText('');
        }
      };

      recognitionRef.current.onerror = (event) => {
        // Silently ignore common non-error cases
        if (event.error === 'no-speech' || event.error === 'aborted') {
          return;
        }
        // Show error for actual problems
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please allow microphone access or type your instructions.');
          setIsRecording(false);
        } else if (event.error === 'network') {
          setError('Network error. Please type your instructions instead.');
        }
      };

      recognitionRef.current.onend = () => {
        // Only restart if actively recording (use ref for reliable state)
        if (isRecordingRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            console.log('Recognition already started');
          }
        }
      };
    } else {
      setError('Speech recognition not supported in this browser. Please use Chrome.');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [isRecording]);

  // Start recording (append mode - doesn't clear existing text)
  const startRecording = async (appendMode = true) => {
    setError('');
    // Only clear if not appending
    if (!appendMode) {
      setOriginalText('');
      setTranslatedText('');
    }
    audioChunksRef.current = [];

    // Set language for speech recognition
    const langConfig = LANGUAGES.find(l => l.code === sourceLang);
    if (recognitionRef.current && langConfig) {
      recognitionRef.current.lang = langConfig.speechCode;
    }

    // Start audio recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Could not access microphone. On mobile, microphone requires HTTPS. Please type your instructions below instead.');
      return;
    }

    // Start speech recognition
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        isRecordingRef.current = true; // Set ref for reliable state tracking
      } catch (e) {
        setError('Could not start speech recognition');
      }
    }
  };

  // Stop recording
  const stopRecording = (autoTranslate = false) => {
    setIsRecording(false);
    isRecordingRef.current = false; // Clear ref to prevent auto-restart

    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    // If voice-to-voice mode, auto translate and speak
    if (autoTranslate || voiceToVoiceMode) {
      setTimeout(() => {
        handleVoiceToVoice();
      }, 500); // Wait for text to be finalized
    }
  };

  // Voice-to-Voice: Auto translate and speak
  const handleVoiceToVoice = async () => {
    // Wait a bit more for final text
    await new Promise(resolve => setTimeout(resolve, 300));

    const textToTranslate = originalText.trim();
    if (!textToTranslate) {
      setError('No speech detected. Please try again and speak clearly.');
      setIsProcessing(false);
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Step 1: Translate
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: textToTranslate,
          sourceLang,
          targetLang
        })
      });

      const data = await response.json();

      if (data.success && data.translatedText) {
        setTranslatedText(data.translatedText);

        // Step 2: Auto-speak the translation
        await speakText(data.translatedText, targetLang);
      } else {
        setError('Translation failed. Please try again.');
      }
    } catch (err) {
      console.error('Voice-to-voice error:', err);
      setError('Translation failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Translate text using server-side API (avoids CORS issues, uses Google Translate)
  const translateText = async () => {
    if (!originalText.trim()) {
      setError('Please record some voice first');
      return;
    }

    setIsTranslating(true);
    setError('');

    const textToTranslate = originalText.trim();
    console.log(`Translating from ${sourceLang} to ${targetLang}:`, textToTranslate);

    try {
      // Use server-side translation API (Google Translate)
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: textToTranslate,
          sourceLang,
          targetLang
        })
      });

      const data = await response.json();
      console.log('Translation response:', data);

      if (data.success && data.translatedText) {
        setTranslatedText(data.translatedText);
        console.log(`Translation successful (${data.source}):`, data.translatedText);
      } else {
        // Use fallback text if translation failed
        setError(data.message || 'Translation service unavailable');
        setTranslatedText(data.translatedText || textToTranslate);
      }
    } catch (err) {
      console.error('Translation error:', err);
      setError('Translation failed. Please try again.');
      setTranslatedText(textToTranslate);
    } finally {
      setIsTranslating(false);
    }
  };

  // Text-to-Speech - uses server-side TTS for Indian languages (no native voices available)
  const speakText = async (text, langCode) => {
    if (!text) {
      setError('No text to speak');
      return;
    }

    setIsSpeaking(true);
    setError('');

    // For Indian languages, use server-side TTS (Google Translate TTS)
    const indianLanguages = ['hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'];
    if (indianLanguages.includes(langCode)) {
      try {
        console.log('Using server TTS for', langCode);
        await speakWithServerTTS(text, langCode);
        return;
      } catch (err) {
        console.error('Server TTS failed:', err);
        // Fall back to browser TTS
      }
    }

    // Use browser TTS for English and as fallback
    try {
      await speakWithEnhancedTTS(text, langCode);
    } catch (err) {
      console.error('TTS failed:', err);
      setError('Could not play audio. Please try again.');
      setIsSpeaking(false);
    }
  };

  // Server-side TTS using Google Translate TTS API
  const speakWithServerTTS = async (text, langCode) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    console.log('Fetching TTS audio from server for:', langCode, 'text:', text.substring(0, 50));
    const response = await fetch(`${API_URL}/tts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, lang: langCode })
    });

    console.log('TTS response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('TTS request failed:', errorText);
      throw new Error('TTS request failed');
    }

    const audioBlob = await response.blob();
    console.log('Audio blob size:', audioBlob.size, 'type:', audioBlob.type);

    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);

    // Pre-load the audio
    audio.preload = 'auto';

    console.log('Playing TTS audio...');
    return new Promise((resolve, reject) => {
      audio.oncanplaythrough = () => {
        console.log('Audio can play through');
      };

      audio.onended = () => {
        console.log('TTS audio finished');
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        resolve();
      };

      audio.onerror = (e) => {
        console.error('Audio playback error:', e, audio.error);
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        reject(e);
      };

      audio.play()
        .then(() => console.log('Audio play started'))
        .catch((err) => {
          console.error('Audio play() failed:', err);
          setIsSpeaking(false);
          reject(err);
        });
    });
  };

  // Enhanced TTS with better voice selection for Edge/Chrome (for English)
  const speakWithEnhancedTTS = (text, langCode) => {
    return new Promise((resolve, reject) => {
      // Cancel any ongoing speech first
      window.speechSynthesis.cancel();

      // Language code mapping for Indian languages
      const langMap = {
        'hi': 'hi-IN',
        'ta': 'ta-IN',
        'te': 'te-IN',
        'kn': 'kn-IN',
        'ml': 'ml-IN',
        'mr': 'mr-IN',
        'bn': 'bn-IN',
        'gu': 'gu-IN',
        'pa': 'pa-IN',
        'en': 'en-IN'
      };

      const targetLangCode = langMap[langCode] || langCode;

      // Function to actually speak once we have voices
      const doSpeak = (voices) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = targetLangCode;
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 1;

        console.log('Looking for voice with lang:', targetLangCode);
        console.log('Available voices:', voices.length);

        // Log ALL voices for debugging
        console.log('All voices:', voices.map(v => `${v.name} (${v.lang})`));

        // Filter voices for target language
        const targetVoices = voices.filter(v =>
          v.lang === targetLangCode ||
          v.lang.startsWith(langCode + '-') ||
          v.lang.startsWith(langCode)
        );
        console.log(`Voices for ${langCode}:`, targetVoices.map(v => `${v.name} (${v.lang})`));

        // Voice selection priority for target language
        let selectedVoice = null;

        // 1. Exact language match with Online/Natural voice (best quality - Edge)
        selectedVoice = voices.find(v =>
          (v.lang === targetLangCode || v.lang.startsWith(langCode + '-')) &&
          (v.name.includes('Online') || v.name.includes('Natural'))
        );

        // 2. Exact language match (e.g., 'hi-IN')
        if (!selectedVoice) {
          selectedVoice = voices.find(v => v.lang === targetLangCode);
        }

        // 3. Language code match (e.g., 'hi' or 'hi-*')
        if (!selectedVoice) {
          selectedVoice = voices.find(v =>
            v.lang.startsWith(langCode + '-') || v.lang === langCode
          );
        }

        // 4. Google voice for target language
        if (!selectedVoice) {
          selectedVoice = voices.find(v =>
            v.name.toLowerCase().includes('google') &&
            (v.lang.startsWith(langCode) || v.lang === targetLangCode)
          );
        }

        // 5. For Hindi - look for Hindi-specific voice names
        if (!selectedVoice && langCode === 'hi') {
          selectedVoice = voices.find(v =>
            v.name.toLowerCase().includes('hindi') ||
            (v.name.toLowerCase().includes('swara') && !v.lang.startsWith('en'))
          );
        }

        // 6. Any voice that mentions the language name
        if (!selectedVoice) {
          const langName = LANGUAGES.find(l => l.code === langCode)?.name.toLowerCase();
          if (langName) {
            selectedVoice = voices.find(v => v.name.toLowerCase().includes(langName));
          }
        }

        // 7. Last resort - Indian English (better than nothing for Indian languages)
        if (!selectedVoice && ['hi', 'ta', 'te', 'kn', 'ml', 'mr', 'bn', 'gu', 'pa'].includes(langCode)) {
          selectedVoice = voices.find(v => v.lang === 'en-IN');
          if (selectedVoice) {
            console.log('Warning: No native voice found, using Indian English');
          }
        }

        // 8. Any English voice as absolute last resort
        if (!selectedVoice) {
          selectedVoice = voices.find(v => v.lang.startsWith('en'));
        }

        if (selectedVoice) {
          console.log('Selected voice:', selectedVoice.name, selectedVoice.lang);
          utterance.voice = selectedVoice;
        } else {
          console.log('No matching voice found, using browser default for', targetLangCode);
        }

        utterance.onstart = () => {
          console.log('Speech started');
        };

        utterance.onend = () => {
          console.log('Speech finished');
          setIsSpeaking(false);
          resolve();
        };

        utterance.onerror = (event) => {
          console.error('Speech error:', event.error);
          setIsSpeaking(false);
          if (event.error !== 'interrupted' && event.error !== 'canceled') {
            reject(new Error(event.error));
          } else {
            resolve();
          }
        };

        // Edge needs a slight delay after cancel
        setTimeout(() => {
          console.log('Starting speech synthesis...');
          window.speechSynthesis.speak(utterance);

          // Edge workaround: pause and resume to trigger speech
          setTimeout(() => {
            if (window.speechSynthesis.speaking) {
              window.speechSynthesis.pause();
              window.speechSynthesis.resume();
            }
          }, 100);
        }, 200);
      };

      // Get voices - may need to wait for them to load
      let voices = window.speechSynthesis.getVoices();

      if (voices.length > 0) {
        doSpeak(voices);
      } else {
        // Voices not loaded yet - wait for them
        console.log('Waiting for voices to load...');

        const voicesChanged = () => {
          voices = window.speechSynthesis.getVoices();
          if (voices.length > 0) {
            window.speechSynthesis.onvoiceschanged = null;
            doSpeak(voices);
          }
        };

        window.speechSynthesis.onvoiceschanged = voicesChanged;

        // Fallback timeout - try speaking anyway after 1 second
        setTimeout(() => {
          voices = window.speechSynthesis.getVoices();
          if (voices.length === 0) {
            console.log('No voices loaded after timeout, trying anyway');
          }
          window.speechSynthesis.onvoiceschanged = null;
          doSpeak(voices);
        }, 1000);
      }
    });
  };

  // Stop speaking
  const stopSpeaking = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  };

  // Save voice note - saves translated text as main instruction for rider
  const handleSave = () => {
    if (onSave) {
      // If translated, save translated text as main content for rider
      // Also keep original for reference
      onSave({
        originalText: originalText.trim(),
        translatedText: translatedText.trim(),
        // The "text" field is what gets shown to rider - use translated if available
        text: translatedText.trim() || originalText.trim(),
        sourceLang,
        targetLang,
        audioBlob
      });
    }
  };

  // Play recorded audio - make sure speech recognition is stopped first
  const playRecordedAudio = () => {
    if (audioBlob) {
      // Stop any active speech recognition to prevent feedback loop
      isRecordingRef.current = false; // Prevent auto-restart
      setIsRecording(false);

      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore if not running
        }
      }

      const audio = new Audio(URL.createObjectURL(audioBlob));
      audio.play();
    }
  };

  return (
    <div className="voice-note-container">
      <h3 className="voice-note-title">
        {mode === 'record' ? '🎤 Voice Delivery Instructions' : '🔊 Delivery Instructions'}
      </h3>

      {error && !isMobileHTTP && <div className="voice-note-error">{error}</div>}

      {mode === 'record' && (
        <>
          {/* Mode Toggle */}
          <div className="mode-toggle">
            <button
              className={`mode-btn ${!voiceToVoiceMode ? 'active' : ''}`}
              onClick={() => setVoiceToVoiceMode(false)}
            >
              📝 Text Mode
            </button>
            <button
              className={`mode-btn ${voiceToVoiceMode ? 'active' : ''}`}
              onClick={() => setVoiceToVoiceMode(true)}
            >
              🔄 Voice-to-Voice
            </button>
          </div>

          {/* Language Selection */}
          <div className="language-selection">
            <div className="language-row">
              <div className="language-select-group">
                <label>Speak in:</label>
                <select
                  value={sourceLang}
                  onChange={(e) => setSourceLang(e.target.value)}
                  disabled={isRecording || isProcessing}
                >
                  {LANGUAGES.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>

              <div className="language-arrow">→</div>

              <div className="language-select-group">
                <label>Translate to:</label>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  disabled={isRecording || isProcessing}
                >
                  {LANGUAGES.filter(l => l.code !== sourceLang).map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="processing-indicator">
              <span className="spinner"></span>
              <span>Translating and preparing audio...</span>
            </div>
          )}

          {/* Voice-to-Voice Mode Recording */}
          {voiceToVoiceMode && !isMobileHTTP && (
            <div className="voice-to-voice-section">
              <p className="v2v-instruction">
                Press and hold to speak in {LANGUAGES.find(l => l.code === sourceLang)?.name},
                release to hear in {LANGUAGES.find(l => l.code === targetLang)?.name}
              </p>

              {!isRecording && !isProcessing ? (
                <button
                  className="v2v-record-btn"
                  onMouseDown={() => startRecording(false)}
                  onMouseUp={() => stopRecording(true)}
                  onTouchStart={() => startRecording(false)}
                  onTouchEnd={() => stopRecording(true)}
                  disabled={isProcessing}
                >
                  <span className="v2v-icon">🎤</span>
                  <span>Hold to Speak</span>
                </button>
              ) : isRecording ? (
                <button
                  className="v2v-record-btn recording"
                  onMouseUp={() => stopRecording(true)}
                  onTouchEnd={() => stopRecording(true)}
                >
                  <span className="v2v-icon pulse">🔴</span>
                  <span>Release to Translate</span>
                </button>
              ) : null}

              {/* Quick retry button */}
              {translatedText && !isRecording && !isProcessing && (
                <div className="v2v-actions">
                  <button
                    className="v2v-action-btn"
                    onClick={() => speakText(translatedText, targetLang)}
                    disabled={isSpeaking}
                  >
                    🔊 Listen Again
                  </button>
                  <button
                    className="v2v-action-btn"
                    onClick={() => {
                      setOriginalText('');
                      setTranslatedText('');
                    }}
                  >
                    🔄 Try Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Standard Recording Controls */}
          {!voiceToVoiceMode && !isMobileHTTP && (
            <div className="recording-controls">
              {!isRecording ? (
                <>
                  <button
                    className="record-btn start"
                    onClick={() => startRecording(true)}
                  >
                    <span className="mic-icon">🎙️</span>
                    {originalText ? 'Add More' : 'Start Recording'}
                  </button>
                  {originalText && (
                    <button
                      className="record-btn clear"
                      onClick={() => {
                        setOriginalText('');
                        setTranslatedText('');
                        setAudioBlob(null);
                      }}
                    >
                      🗑️ Clear All
                    </button>
                  )}
                </>
              ) : (
                <button
                  className="record-btn stop"
                  onClick={() => stopRecording(false)}
                >
                  <span className="recording-indicator"></span>
                  Stop Recording
                </button>
              )}
            </div>
          )}

          {isMobileHTTP && (
            <div className="mobile-notice" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '15px',
              fontSize: '14px',
              textAlign: 'center'
            }}>
              <strong>Type your instructions below</strong><br/>
              <small>(Voice recording requires HTTPS on mobile)</small>
            </div>
          )}

          {/* Show interim text while recording */}
          {isRecording && interimText && (
            <div className="interim-text">
              <span className="listening-icon">👂</span> {interimText}
            </div>
          )}

          {/* Text Input - shows voice recording or allows typing */}
          <div className="manual-input-section">
            <label>Your Message ({LANGUAGES.find(l => l.code === sourceLang)?.name}):</label>
            <textarea
              className="manual-text-input"
              placeholder="Record voice or type delivery instructions here..."
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              rows={4}
            />
            {originalText && (
              <div className="text-actions" style={{ marginTop: '10px' }}>
                <button onClick={playRecordedAudio} disabled={!audioBlob}>
                  🔊 Play Recording
                </button>
                <button onClick={() => speakText(originalText, sourceLang)}>
                  🗣️ Read Aloud
                </button>
              </div>
            )}
          </div>

          {/* Translation Section */}
          {originalText && (
            <div className="translation-section">
              <div className="language-select-group">
                <label>Translate to:</label>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                >
                  {LANGUAGES.filter(l => l.code !== sourceLang).map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                  ))}
                </select>
              </div>

              <button
                className="translate-btn"
                onClick={translateText}
                disabled={isTranslating}
              >
                {isTranslating ? '⏳ Translating...' : '🌐 Translate'}
              </button>
            </div>
          )}

          {/* Translated Text Display */}
          {translatedText && (
            <div className="text-display translated">
              <div className="text-label">
                Translated ({LANGUAGES.find(l => l.code === targetLang)?.name}):
              </div>
              <div className="text-content">{translatedText}</div>
              <div className="text-actions">
                {!isSpeaking ? (
                  <button onClick={() => speakText(translatedText, targetLang)}>
                    🔊 Listen in {LANGUAGES.find(l => l.code === targetLang)?.name}
                  </button>
                ) : (
                  <button onClick={stopSpeaking} className="stop-btn">
                    ⏹️ Stop
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Save Button */}
          {originalText && (
            <button className="save-btn" onClick={handleSave}>
              💾 Save Voice Note
            </button>
          )}
        </>
      )}

      {/* Playback mode for riders */}
      {mode === 'play' && (
        <div className="playback-mode">
          <div className="language-select-group">
            <label>Your Preferred Language:</label>
            <select
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.code} value={lang.code}>{lang.name}</option>
              ))}
            </select>
          </div>

          {originalText && (
            <>
              <div className="text-display original">
                <div className="text-label">Original Message:</div>
                <div className="text-content">{originalText}</div>
              </div>

              <button
                className="translate-btn"
                onClick={translateText}
                disabled={isTranslating}
              >
                {isTranslating ? '⏳ Translating...' : `🌐 Translate to ${LANGUAGES.find(l => l.code === targetLang)?.name}`}
              </button>

              {translatedText && (
                <div className="text-display translated">
                  <div className="text-label">
                    In {LANGUAGES.find(l => l.code === targetLang)?.name}:
                  </div>
                  <div className="text-content">{translatedText}</div>
                  <button
                    className={`listen-btn ${isSpeaking ? 'speaking' : ''}`}
                    onClick={() => isSpeaking ? stopSpeaking() : speakText(translatedText, targetLang)}
                  >
                    {isSpeaking ? '⏹️ Stop' : '🔊 Listen'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceNote;
